/**
 * Value-builders tab.
 *
 * Reads/writes GET|PUT /api/admin/value-builders:
 *  - approved_usps: a full structured list editor (family / label / guidance
 *    rows; add / delete / reorder).
 *  - manager_review_below: send a call to manager review when its score is below
 *    this number.
 *  - manager_review_critical_misses: multiselect from `critical_miss_options`.
 *  - scoring_excludes_call_types: multiselect from `call_type_options`.
 * The option lists come from the server so they stay authoritative.
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { ApprovedUsp } from "@/types";
import { Button, Card } from "@/components/ui";
import { LoadingState } from "@/components/LoadingState";
import { ErrorState } from "@/components/ErrorState";
import {
  SavedNote,
  ErrorNote,
  NumberField,
  IconButton,
  moveItem,
} from "./shared";

const VALUE_BUILDERS_KEY = ["admin", "value-builders"] as const;

// Friendly labels for the option codes the server returns.
const OPTION_LABELS: Record<string, string> = {
  // call types
  automotive_tint: "Automotive tint",
  ppf: "Paint protection film (PPF)",
  arch_residential: "Architectural — residential",
  arch_commercial: "Architectural — commercial",
  non_sales_admin: "Non-sales / admin",
  // critical misses
  no_next_step: "No next step",
  rude_tone: "Rude tone",
  misleading_claim: "Misleading claim",
  bad_staff_attribution: "Bad staff attribution",
};
function optionLabel(code: string): string {
  return OPTION_LABELS[code] ?? code;
}

const BLANK_USP: ApprovedUsp = { family: "", label: "", guidance: "" };

export function ValueBuildersTab() {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: VALUE_BUILDERS_KEY,
    queryFn: ({ signal }) => api.adminGetValueBuilders(signal),
  });

  const [usps, setUsps] = useState<ApprovedUsp[]>([]);
  const [reviewBelow, setReviewBelow] = useState(0);
  const [criticalMisses, setCriticalMisses] = useState<string[]>([]);
  const [excludedTypes, setExcludedTypes] = useState<string[]>([]);
  const [justSaved, setJustSaved] = useState(false);

  const data = query.data;
  useEffect(() => {
    if (data) {
      setUsps(data.approved_usps);
      setReviewBelow(data.manager_review_below);
      setCriticalMisses(data.manager_review_critical_misses);
      setExcludedTypes(data.scoring_excludes_call_types);
      setJustSaved(false);
    }
  }, [data]);

  const save = useMutation({
    mutationFn: (body: {
      approved_usps?: ApprovedUsp[];
      manager_review_below?: number;
      manager_review_critical_misses?: string[];
      scoring_excludes_call_types?: string[];
    }) => api.adminSaveValueBuilders(body),
    onSuccess: () => {
      // PUT returns {ok,changed}; the GET refetch supplies the authoritative config.
      void qc.invalidateQueries({ queryKey: VALUE_BUILDERS_KEY });
      setJustSaved(true);
    },
  });

  if (query.isLoading) {
    return <LoadingState label="Loading value-builders settings…" />;
  }
  if (query.isError || !data) {
    return (
      <ErrorState error={query.error} onRetry={() => void query.refetch()} />
    );
  }

  const sameArr = (a: string[], b: string[]) =>
    a.length === b.length && a.every((v, i) => v === b[i]);
  const uspsDirty =
    usps.length !== data.approved_usps.length ||
    usps.some((u, i) => {
      const o = data.approved_usps[i];
      return (
        !o ||
        u.family !== o.family ||
        u.label !== o.label ||
        u.guidance !== o.guidance
      );
    });
  const dirty =
    uspsDirty ||
    reviewBelow !== data.manager_review_below ||
    !sameArr(criticalMisses, data.manager_review_critical_misses) ||
    !sameArr(excludedTypes, data.scoring_excludes_call_types);
  const saving = save.isPending;

  function touch() {
    if (justSaved) setJustSaved(false);
  }
  function updateUsp(i: number, patch: Partial<ApprovedUsp>) {
    touch();
    setUsps((rows) =>
      rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)),
    );
  }
  function removeUsp(i: number) {
    touch();
    setUsps((rows) => rows.filter((_, idx) => idx !== i));
  }
  function addUsp() {
    touch();
    setUsps((rows) => [...rows, { ...BLANK_USP }]);
  }
  function reorderUsp(from: number, to: number) {
    touch();
    setUsps((rows) => moveItem(rows, from, to));
  }
  function toggle(
    set: (fn: (prev: string[]) => string[]) => void,
    code: string,
  ) {
    touch();
    set((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">Value-builders</h2>
          <p className="text-sm text-muted-foreground">
            Approved talking points and the review thresholds that flag calls for
            a manager.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <SavedNote show={justSaved && !dirty} />
          <Button
            onClick={() => {
              setJustSaved(false);
              save.mutate({
                approved_usps: usps,
                manager_review_below: reviewBelow,
                manager_review_critical_misses: criticalMisses,
                scoring_excludes_call_types: excludedTypes,
              });
            }}
            disabled={saving || !dirty}
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>

      {/* Approved USPs */}
      <Card className="flex flex-col gap-3">
        <h2 className="text-sm font-semibold">Approved USPs</h2>
        <p className="text-sm text-muted-foreground">
          Unique selling points the grader credits when a rep builds value.
        </p>

        <div className="flex flex-col gap-3">
          {usps.map((u, i) => (
            <div key={i} className="rounded-md border p-3">
              <div className="flex items-start gap-2">
                <div className="flex flex-col gap-1 pt-5">
                  <IconButton
                    title="Move up"
                    onClick={() => reorderUsp(i, i - 1)}
                    disabled={i === 0}
                  >
                    ↑
                  </IconButton>
                  <IconButton
                    title="Move down"
                    onClick={() => reorderUsp(i, i + 1)}
                    disabled={i === usps.length - 1}
                  >
                    ↓
                  </IconButton>
                </div>

                <div className="grid flex-1 gap-3 sm:grid-cols-2">
                  <label className="flex flex-col gap-1 text-sm">
                    <span className="font-medium">Family</span>
                    <input
                      value={u.family}
                      onChange={(e) => updateUsp(i, { family: e.target.value })}
                      placeholder="warranty"
                      className="rounded-md border bg-background px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring"
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-sm">
                    <span className="font-medium">Label</span>
                    <input
                      value={u.label}
                      onChange={(e) => updateUsp(i, { label: e.target.value })}
                      placeholder="Lifetime warranty"
                      className="rounded-md border bg-background px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring"
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-sm sm:col-span-2">
                    <span className="font-medium">Guidance</span>
                    <textarea
                      value={u.guidance}
                      onChange={(e) =>
                        updateUsp(i, { guidance: e.target.value })
                      }
                      rows={2}
                      spellCheck={false}
                      placeholder="When/how the rep should use this point…"
                      className="w-full rounded-md border bg-background px-3 py-2 text-xs leading-relaxed outline-none focus:ring-2 focus:ring-ring"
                    />
                  </label>
                </div>

                <div className="pt-5">
                  <IconButton title="Delete USP" onClick={() => removeUsp(i)}>
                    ✕
                  </IconButton>
                </div>
              </div>
            </div>
          ))}
          {usps.length === 0 && (
            <p className="text-sm text-muted-foreground">No USPs yet.</p>
          )}
        </div>

        <div>
          <Button variant="ghost" className="text-xs" onClick={addUsp}>
            + Add USP
          </Button>
        </div>
      </Card>

      {/* Review thresholds */}
      <Card className="flex flex-col gap-4">
        <h2 className="text-sm font-semibold">Manager review</h2>

        <NumberField
          label="Flag for review when score is below"
          value={reviewBelow}
          min={0}
          onChange={(v) => {
            touch();
            setReviewBelow(Math.max(0, Math.round(v)));
          }}
          className="max-w-[160px]"
        />

        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium">
            Also flag on these critical misses
          </span>
          <div className="flex flex-wrap gap-2">
            {data.critical_miss_options.map((code) => {
              const on = criticalMisses.includes(code);
              return (
                <button
                  key={code}
                  type="button"
                  onClick={() => toggle(setCriticalMisses, code)}
                  className={
                    "rounded-full border px-3 py-1 text-sm transition " +
                    (on
                      ? "border-primary bg-primary text-primary-foreground"
                      : "hover:bg-accent")
                  }
                >
                  {optionLabel(code)}
                </button>
              );
            })}
          </div>
        </div>
      </Card>

      {/* Excluded call types */}
      <Card className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold">Excluded from scoring</h2>
        <p className="text-sm text-muted-foreground">
          Call types that are never graded (excluded from stats).
        </p>
        <div className="flex flex-wrap gap-2">
          {data.call_type_options.map((code) => {
            const on = excludedTypes.includes(code);
            return (
              <button
                key={code}
                type="button"
                onClick={() => toggle(setExcludedTypes, code)}
                className={
                  "rounded-full border px-3 py-1 text-sm transition " +
                  (on
                    ? "border-primary bg-primary text-primary-foreground"
                    : "hover:bg-accent")
                }
              >
                {optionLabel(code)}
              </button>
            );
          })}
        </div>
      </Card>

      <div className="flex items-center gap-3">
        <ErrorNote show={save.isError} />
      </div>
    </div>
  );
}
